import { RmsBadgeTemplateData } from '@/backend/features/badging/badging-configuration/types';
import { showContactUsModal } from '@/ui/utils/showContactUsModal';
import { AccountBadgeColumns, AccountBadgeData } from '@juvare/badging/backend/features/badges/shared';
import {
    AccountBadgeSlideoutBaseProps,
    AccountBadgeSlideoutProps,
} from '@juvare/badging/ui/features/badge-management/components/AccountBadgeSlideout/types/AccountBadgeSlideoutTypes';
import { AccountBadgeContextProviderProps } from '@juvare/badging/ui/features/badge-management/contexts/AccountBadgeContext';
import { showAccountBadgePreviewModal } from '@juvare/badging/ui/features/badge-preview/utils/showAccountBadgePreviewModal';
import { GetResponse } from '@juvare/platform-shared/features/storage';
import { Button } from '@mui/material';
import { TFunction } from 'i18next';
import React, { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';

export function useAccountBadgeSlideout(): {
    buildAccountBadgeSlideoutComponent: AccountBadgeContextProviderProps['buildAccountBadgeSlideoutComponent'];
    buildAccountBadgeSlideoutAccountSectionComponent: AccountBadgeContextProviderProps['buildAccountBadgeSlideoutComponent'];
} {
    const { t } = useTranslation('rms');

    const getAccountBadgeSlideoutDataElements: AccountBadgeSlideoutProps['getAccountBadgeSlideoutDataElements'] = (accountBadge) => {
        return getSlideoutDataElements(t, accountBadge);
    };

    const buildAccountBadgeSlideoutComponent = (
        AccountBadgeSlideoutComponent: ComponentType<AccountBadgeSlideoutProps>,
    ): React.FC<AccountBadgeSlideoutBaseProps> => {
        return (props) => (
            <AccountBadgeSlideoutComponent
                accountBadgeId={props.accountBadgeId}
                onClose={props.onClose}
                refetchAccountBadgeList={props.refetchAccountBadgeList}
                getAccountBadgeSlideoutDataElements={getAccountBadgeSlideoutDataElements}
                onAccountBadgeAccountDetailsEdit={undefined}
            />
        );
    };

    const buildAccountBadgeSlideoutAccountSectionComponent = (
        AccountBadgeSlideoutComponent: ComponentType<AccountBadgeSlideoutProps>,
    ): React.FC<AccountBadgeSlideoutBaseProps> => {
        const AccountBadgeSlideoutAccountSectionComponent: React.FC<AccountBadgeSlideoutBaseProps> = (props) => {
            const renderCustomBottomActions: AccountBadgeSlideoutProps['renderCustomBottomActions'] = (props) => {
                const actionElements: React.ReactElement[] = [];

                if (props.accountBadge.columns.isStatusValid) {
                    actionElements.push(
                        <Button
                            variant='outlined'
                            size='large'
                            color='info'
                            onClick={() => {
                                showAccountBadgePreviewModal(props.accountBadge);
                            }}
                            sx={{ width: '100%' }}
                        >
                            {t('badging.accountBadgeSection.actions.preview')}
                        </Button>,
                    );

                    if (props.accountBadgeActions.canRevoke) {
                        actionElements.push(
                            <Button
                                variant='outlined'
                                size='large'
                                color='info'
                                onClick={() => props.accountBadgeActions.handleAccountBadgeRevoke()}
                                sx={{ width: '100%' }}
                            >
                                {t('badging.accountBadgeSection.actions.revoke')}
                            </Button>,
                        );
                    }
                } else {
                    actionElements.push(
                        <Button
                            variant='outlined'
                            size='large'
                            color='info'
                            onClick={() => {
                                showContactUsModal(t);
                            }}
                            sx={{ width: '100%' }}
                        >
                            {t('badging.accountBadgeSection.actions.contactUsButton')}
                        </Button>,
                    );
                }

                return actionElements;
            };

            return (
                <AccountBadgeSlideoutComponent
                    accountBadgeId={props.accountBadgeId}
                    onClose={props.onClose}
                    refetchAccountBadgeList={props.refetchAccountBadgeList}
                    getAccountBadgeSlideoutDataElements={getAccountBadgeSlideoutDataElements}
                    onAccountBadgeAccountDetailsEdit={undefined}
                    renderCustomTopMenuActions={() => []}
                    renderCustomTopActions={() => []}
                    renderCustomBottomActions={renderCustomBottomActions}
                    disableStatusLabelActions
                />
            );
        };

        return AccountBadgeSlideoutAccountSectionComponent;
    };

    return {
        buildAccountBadgeSlideoutComponent,
        buildAccountBadgeSlideoutAccountSectionComponent,
    };
}

function getSlideoutDataElements(t: TFunction<'rms', undefined>, accountBadge: GetResponse<AccountBadgeData, AccountBadgeColumns>) {
    const imageTemplateDataSnapshot = accountBadge.data.templateDataSnapshot as RmsBadgeTemplateData | undefined;
    const firstName = imageTemplateDataSnapshot?.nameFirstLine;
    const lastName = imageTemplateDataSnapshot?.nameSecondLine;
    const affiliation = imageTemplateDataSnapshot?.affiliation;
    const occupation = imageTemplateDataSnapshot?.occupation;
    const organization = imageTemplateDataSnapshot?.organization;
    const dateOfBirth = imageTemplateDataSnapshot?.dateOfBirth;
    const height = imageTemplateDataSnapshot?.height;
    const eyeColor = imageTemplateDataSnapshot?.eyeColor;

    return [
        {
            label: t('common.badgeName'),
            value: [firstName, lastName].filter(Boolean).join(', ') || '-',
        },
        {
            label: t('common.affiliation'),
            value: affiliation,
        },
        {
            label: t('common.organization'),
            value: organization,
        },
        {
            label: t('common.occupation'),
            value: occupation,
        },
        {
            label: t('common.dateOfBirth'),
            value: dateOfBirth,
        },
        {
            label: t('common.height'),
            value: height,
        },
        {
            label: t('common.eyeColor'),
            value: eyeColor,
        },
    ];
}
